/**
 * The catalogue tree as a filter: Activité → Famille → Sous-famille → Segment.
 *
 * The level names are the installation's own (`/category-levels`), read
 * rather than written here: "Activité" is one distributor's word, and the
 * next one may say "Univers". The brand, the fifth level of the design, is
 * the toolbar's brand select — a brand cuts across the tree rather than
 * sitting under one node.
 *
 * Only the selected branch is unfolded. A tree of a few hundred nodes shown
 * whole is a list nobody reads; the path to the selection, its children and
 * the roots are what the reader needs to go one level down or back up.
 */

import { useTranslation } from "react-i18next";

import type { CategoryLevel, CategoryNode } from "../../api";
import { NAMESPACE } from "../../i18n";

/**
 * Articles at a node and every node under it. The server counts each node's own articles only, so a
 * famille or sous-famille whose articles all sit in its segments read empty, beside an activité and
 * segments that showed numbers. The filter counts what choosing the node shows: all of its branch.
 */
function branchTotals(
  nodes: readonly CategoryNode[],
  into = new Map<string, number>(),
): Map<string, number> {
  for (const node of nodes) {
    branchTotals(node.children, into);
    const below = node.children.reduce(
      (sum, child) => sum + (into.get(child.category.path) ?? 0),
      0,
    );
    into.set(node.category.path, node.article_count + below);
  }
  return into;
}

function isOnPath(path: string, selected: string): boolean {
  return selected === path || selected.startsWith(`${path}/`);
}

export interface CategoryPickerProps {
  roots: readonly CategoryNode[];
  levels: readonly CategoryLevel[];
  selected: string;
  onSelect: (path: string) => void;
}

export function CategoryPicker({ roots, levels, selected, onSelect }: CategoryPickerProps) {
  const { t } = useTranslation(NAMESPACE);
  const levelName = (depth: number) => levels.find((level) => level.depth === depth)?.label ?? "";
  const names = levels.map((level) => level.label).filter(Boolean);
  const totals = branchTotals(roots);

  function branch(nodes: readonly CategoryNode[]) {
    return (
      <ul className="hardauto-tree">
        {nodes.map((node) => {
          const path = node.category.path;
          const open = isOnPath(path, selected);
          return (
            <li key={node.category.id}>
              <button
                type="button"
                className="hardauto-consultation__node"
                aria-pressed={selected === path}
                onClick={() => {
                  // A second click on the selection goes back up one level,
                  // which is the gesture people try first.
                  onSelect(selected === path ? path.split("/").slice(0, -1).join("/") : path);
                }}
              >
                <span className="hardauto-tree__label">{node.category.label}</span>
                {(totals.get(path) ?? 0) > 0 ? (
                  <span className="hardauto-tree__count">{totals.get(path)}</span>
                ) : null}
              </button>
              {open && node.children.length > 0 ? branch(node.children) : null}
            </li>
          );
        })}
      </ul>
    );
  }

  const depth = selected ? selected.split("/").length : 0;

  return (
    <nav className="hardauto-consultation__tree" aria-label={t("consultation.tree")}>
      <p className="hardauto-tree__level">
        {names.length > 0
          ? [...names, t("consultation.brand")].join(" › ")
          : t("consultation.tree")}
      </p>
      <button
        type="button"
        className="hardauto-consultation__node"
        aria-pressed={selected === ""}
        onClick={() => {
          onSelect("");
        }}
      >
        <span className="hardauto-tree__label">{t("consultation.wholeCatalogue")}</span>
      </button>
      {roots.length > 0 ? (
        branch(roots)
      ) : (
        <p className="hardauto-cell__muted">{t("consultation.noTree")}</p>
      )}
      {selected && levelName(depth + 1) ? (
        <p className="hardauto-cell__muted">
          {t("consultation.nextLevel", { level: levelName(depth + 1) })}
        </p>
      ) : null}
    </nav>
  );
}
